/*!
 * @pixi/assets - v6.5.2
 * Compiled Wed, 24 Aug 2022 13:51:19 UTC
 *
 * @pixi/assets is licensed under the MIT License.
 * http://www.opensource.org/licenses/mit-license
 */
import { Texture, BaseTexture, ExtensionType, SVGResource, extensions as extensions$1 } from '@pixi/core';
import { Spritesheet } from '@pixi/spritesheet';
import { path, getResolutionOfUrl } from '@pixi/utils';
import { settings } from '@pixi/settings';
import { BasisParser, BASIS_FORMAT_TO_TYPE, BASIS_FORMATS, TranscoderWorker } from '@pixi/basis';
import { TextFormat, XMLStringFormat, BitmapFont } from '@pixi/text-bitmap';
import { CompressedTextureResource, parseDDS, parseKTX } from '@pixi/compressed-textures';
import { FORMATS, MIPMAP_MODES, ALPHA_MODES } from '@pixi/constants';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        var arguments$1 = arguments;

        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments$1[i];
            for (var p in s) { if (Object.prototype.hasOwnProperty.call(s, p)) { t[p] = s[p]; } }
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) { throw t[1]; } return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) { throw new TypeError("Generator is already executing."); }
        while (_) { try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) { return t; }
            if (y = 0, t) { op = [op[0] & 2, t.value]; }
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) { _.ops.pop(); }
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; } }
        if (op[0] & 5) { throw op[1]; } return { value: op[0] ? op[1] : void 0, done: true };
    }
}

/**
 * Quietly Loads assets in the background.
 * @memberof PIXI
 */
var BackgroundLoader = /** @class */ (function () {
    /**
     * @param loader
     * @param verbose - should the loader log to the console
     */
    function BackgroundLoader(loader, verbose) {
        if (verbose === void 0) { verbose = false; }
        this._loader = loader;
        this._assetList = [];
        this._isLoading = false;
        this._maxConcurrent = 1;
        this.verbose = verbose;
    }
    /**
     * Adds an array of assets to load.
     * @param assetUrls - assets to load
     */
    BackgroundLoader.prototype.add = function (assetUrls) {
        var _this = this;
        assetUrls.forEach(function (a) {
            _this._assetList.push(a);
        });
        // eslint-disable-next-line no-console
        if (this.verbose)
            { console.log('[BackgroundLoader] assets: ', this._assetList); }
        if (this._isActive && !this._isLoading) {
            this._next();
        }
    };
    /**
     * Loads the next set of assets. Will try to load as many assets as it can at the same time.
     *
     * The max assets it will try to load at one time will be 4.
     */
    BackgroundLoader.prototype._next = function () {
        return __awaiter(this, void 0, Promise, function () {
            var toLoad, toLoadAmount, i;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!(this._assetList.length && this._isActive)) { return [3 /*break*/, 2]; }
                        this._isLoading = true;
                        toLoad = [];
                        toLoadAmount = Math.min(this._assetList.length, this._maxConcurrent);
                        for (i = 0; i < toLoadAmount; i++) {
                            toLoad.push(this._assetList.pop());
                        }
                        return [4 /*yield*/, this._loader.load(toLoad)];
                    case 1:
                        _a.sent();
                        this._isLoading = false;
                        this._next();
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    Object.defineProperty(BackgroundLoader.prototype, "active", {
        /**
         * @returns whether the class is active
         */
        get: function () {
            return this._isActive;
        },
        /** Activate/Deactivate the loading. If set to true then it will immediately continue to load the next asset. */
        set: function (value) {
            if (this._isActive === value)
                { return; }
            this._isActive = value;
            if (value && !this._isLoading) {
                this._next();
            }
        },
        enumerable: false,
        configurable: true
    });
    return BackgroundLoader;
}());

var convertToList = function (input, transform) {
    if (!Array.isArray(input)) {
        input = [input];
    }
    if (!transform) {
        return input;
    }
    return input.map(function (item) {
        if (typeof item === 'string') {
            return transform(item);
        }
        return item;
    });
};

function processX(base, ids, depth, result, tags) {
    var id = ids[depth];
    for (var i = 0; i < id.length; i++) {
        var value = id[i];
        if (depth < ids.length - 1) {
            processX(base.replace(result[depth], value), ids, depth + 1, result, tags);
        }
        else {
            tags.push(base.replace(result[depth], value));
        }
    }
}
/**
 * Creates a list of all possible combinations of the given strings.
 * @example
 * const out2 = createStringVariations('name is {chicken,wolf,sheep}');
 * console.log(out2); // [ 'name is chicken', 'name is wolf', 'name is sheep' ]
 * @param string - The string to process
 */
function createStringVariations(string) {
    var regex = /\{(.*?)\}/g;
    var result = string.match(regex);
    var tags = [];
    if (result) {
        var ids_1 = [];
        result.forEach(function (vars) {
            // first remove the brackets...
            var split = vars.substring(1, vars.length - 1).split(',');
            ids_1.push(split);
        });
        processX(string, ids_1, 0, result, tags);
    }
    else {
        tags.push(string);
    }
    return tags;
}

/**
 * Checks if the given value is an array.
 * @param item - The item to test
 */
var isSingleItem = function (item) { return (!Array.isArray(item)); };

/**
 * A single Cache for all assets.
 *
 * When assets are added to the cache via set they normally are added to the cache as key-value pairs.
 *
 * With this cache, you can add parsers that will take the object and convert it to a list of assets that can be cached.
 * for example a cacheSprite Sheet parser will add all of the textures found within its sprite sheet directly to the cache.
 *
 * This gives devs the flexibility to cache any type of object however we want.
 *
 * It is not intended that this class is created by developers - it is part of the Asset package.
 * This is the first major system of PixiJS' main Assets class.
 * @memberof PIXI
 * @class Cache
 */
var CacheClass = /** @class */ (function () {
    function CacheClass() {
        this._parsers = [];
        this._cache = new Map();
        this._cacheMap = new Map();
    }
    /** Clear all entries. */
    CacheClass.prototype.reset = function () {
        this._cacheMap.clear();
        this._cache.clear();
    };
    /**
     * Check if the key exists
     * @param key - The key to check
     */
    CacheClass.prototype.has = function (key) {
        return this._cache.has(key);
    };
    /**
     * Fetch entry by key
     * @param key - The key of the entry to get
     */
    CacheClass.prototype.get = function (key) {
        var result = this._cache.get(key);
        if (!result) {
            console.warn("[Assets] Asset id " + key + " was not found in the Cache");
        }
        return result;
    };
    /**
     * Set a value by key or keys name
     * @param key - The key or keys to set
     * @param value - The value to store in the cache or from which cacheable assets will be derived.
     */
    CacheClass.prototype.set = function (key, value) {
        var _this = this;
        var keys = convertToList(key);
        var cacheableAssets;
        for (var i = 0; i < this.parsers.length; i++) {
            var parser = this.parsers[i];
            if (parser.test(value)) {
                cacheableAssets = parser.getCacheableAssets(keys, value);
                break;
            }
        }
        if (!cacheableAssets) {
            cacheableAssets = {};
            keys.forEach(function (key) {
                cacheableAssets[key] = value;
            });
        }
        var cacheKeys = Object.keys(cacheableAssets);
        var cachedAssets = {
            cacheKeys: cacheKeys,
            keys: keys
        };
        // this is so we can remove them later..
        keys.forEach(function (key) {
            _this._cacheMap.set(key, cachedAssets);
        });
        cacheKeys.forEach(function (key) {
            if (_this._cache.has(key) && _this._cache.get(key) !== value) {
                console.warn('[Cache] already has key:', key);
            }
            _this._cache.set(key, cacheableAssets[key]);
        });
        // temporary to keep compatible with existing texture caching.. until we remove them!
        if (value instanceof Texture) {
            var texture_1 = value;
            keys.forEach(function (key) {
                if (texture_1.baseTexture !== Texture.EMPTY.baseTexture) {
                    BaseTexture.addToCache(texture_1.baseTexture, key);
                }
                Texture.addToCache(texture_1, key);
            });
        }
    };
    /**
     * Remove entry by key
     *
     * This function will also remove any associated alias from the cache also.
     * @param key - The key of the entry to remove
     */
    CacheClass.prototype.remove = function (key) {
        var _this = this;
        this._cacheMap.get(key);
        if (!this._cacheMap.has(key)) {
            console.warn("[Assets] Asset id " + key + " was not found in the Cache");
            return;
        }
        var cacheMap = this._cacheMap.get(key);
        var cacheKeys = cacheMap.cacheKeys;
        cacheKeys.forEach(function (key) {
            _this._cache.delete(key);
        });
        cacheMap.keys.forEach(function (key) {
            _this._cacheMap.delete(key);
        });
    };
    Object.defineProperty(CacheClass.prototype, "parsers", {
        /** All loader parsers registered */
        get: function () {
            return this._parsers;
        },
        enumerable: false,
        configurable: true
    });
    return CacheClass;
}());
var Cache = new CacheClass();

function getCacheableAssets(keys, asset, ignoreMultiPack) {
    var out = {};
    keys.forEach(function (key) {
        out[key] = asset;
    });
    Object.keys(asset.textures).forEach(function (key) {
        out[key] = asset.textures[key];
    });
    if (!ignoreMultiPack) {
        var basePath_1 = path.dirname(keys[0]);
        asset.linkedSheets.forEach(function (item, i) {
            var out2 = getCacheableAssets([basePath_1 + "/" + asset.data.meta.related_multi_packs[i]], item, true);
            Object.assign(out, out2);
        });
    }
    return out;
}
var cacheSpritesheet = {
    extension: ExtensionType.CacheParser,
    test: function (asset) { return asset instanceof Spritesheet; },
    getCacheableAssets: function (keys, asset) { return getCacheableAssets(keys, asset, false); }
};

var cacheTextureArray = {
    extension: ExtensionType.CacheParser,
    test: function (asset) { return Array.isArray(asset) && asset.every(function (t) { return t instanceof Texture; }); },
    getCacheableAssets: function (keys, asset) {
        var out = {};
        keys.forEach(function (key) {
            asset.forEach(function (item, i) {
                out[key + (i === 0 ? '' : i + 1)] = item;
            });
        });
        return out;
    }
};

function addFormats() {
    var arguments$1 = arguments;

    var _this = this;
    var format = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        format[_i] = arguments$1[_i];
    }
    return function (formats) { return __awaiter(_this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            formats.unshift.apply(formats, format);
            return [2 /*return*/, formats];
        });
    }); };
}
function removeFormats() {
    var arguments$1 = arguments;

    var _this = this;
    var format = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        format[_i] = arguments$1[_i];
    }
    return function (formats) { return __awaiter(_this, void 0, void 0, function () {
        var _i, format_1, f, index;
        return __generator(this, function (_a) {
            for (_i = 0, format_1 = format; _i < format_1.length; _i++) {
                f = format_1[_i];
                index = formats.indexOf(f);
                if (index !== -1) {
                    formats.splice(index, 1);
                }
            }
            return [2 /*return*/, formats];
        });
    }); };
}

var detectAvif = {
    extension: ExtensionType.DetectionParser,
    test: function () { return __awaiter(void 0, void 0, Promise, function () {
        var avifData, blob;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!globalThis.createImageBitmap)
                        { return [2 /*return*/, false]; }
                    avifData = 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgANogQEAwgMg8f8D///8WfhwB8+ErK42A=';
                    return [4 /*yield*/, settings.ADAPTER.fetch(avifData).then(function (r) { return r.blob(); })];
                case 1:
                    blob = _a.sent();
                    return [2 /*return*/, createImageBitmap(blob).then(function () { return true; }, function () { return false; })];
            }
        });
    }); },
    add: addFormats('avif'),
    remove: removeFormats('avif')
};

var storedGl;
var extensions;
function getCompressedTextureExtensions() {
    extensions = {
        s3tc: storedGl.getExtension('WEBGL_compressed_texture_s3tc'),
        s3tc_sRGB: storedGl.getExtension('WEBGL_compressed_texture_s3tc_srgb'),
        etc: storedGl.getExtension('WEBGL_compressed_texture_etc'),
        etc1: storedGl.getExtension('WEBGL_compressed_texture_etc1'),
        pvrtc: storedGl.getExtension('WEBGL_compressed_texture_pvrtc')
            || storedGl.getExtension('WEBKIT_WEBGL_compressed_texture_pvrtc'),
        atc: storedGl.getExtension('WEBGL_compressed_texture_atc'),
        astc: storedGl.getExtension('WEBGL_compressed_texture_astc')
    };
}
var detectCompressedTextures = {
    extension: ExtensionType.DetectionParser,
    test: function () { return __awaiter(void 0, void 0, Promise, function () {
        var canvas, gl;
        return __generator(this, function (_a) {
            canvas = settings.ADAPTER.createCanvas();
            gl = canvas.getContext('webgl');
            if (!gl) {
                console.warn('WebGL not available for compressed textures.');
                return [2 /*return*/, false];
            }
            storedGl = gl;
            return [2 /*return*/, true];
        });
    }); },
    add: function (formats) { return __awaiter(void 0, void 0, Promise, function () {
        var textureFormats, extensionName, extension;
        return __generator(this, function (_a) {
            if (!extensions)
                { getCompressedTextureExtensions(); }
            textureFormats = [];
            // Assign all available compressed-texture formats
            for (extensionName in extensions) {
                extension = extensions[extensionName];
                if (!extension) {
                    continue;
                }
                textureFormats.push(extensionName);
            }
            formats.unshift.apply(formats, textureFormats);
            return [2 /*return*/, formats];
        });
    }); },
    remove: function (formats) { return __awaiter(void 0, void 0, Promise, function () {
        return __generator(this, function (_a) {
            if (!extensions)
                { getCompressedTextureExtensions(); }
            return [2 /*return*/, formats.filter(function (f) { return !(f in extensions); })];
        });
    }); },
};

var detectBasis = {
    extension: ExtensionType.DetectionParser,
    test: function () { return __awaiter(void 0, void 0, Promise, function () { return __generator(this, function (_a) {
        return [2 /*return*/, !!(BasisParser.basisBinding && BasisParser.TranscoderWorker.wasmSource)];
    }); }); },
    add: addFormats('basis'),
    remove: removeFormats('basis')
};

var detectWebp = {
    extension: ExtensionType.DetectionParser,
    test: function () { return __awaiter(void 0, void 0, Promise, function () {
        var webpData, blob;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!globalThis.createImageBitmap)
                        { return [2 /*return*/, false]; }
                    webpData = 'data:image/webp;base64,UklGRh4AAABXRUJQVlA4TBEAAAAvAAAAAAfQ//73v/+BiOh/AAA=';
                    return [4 /*yield*/, settings.ADAPTER.fetch(webpData).then(function (r) { return r.blob(); })];
                case 1:
                    blob = _a.sent();
                    return [2 /*return*/, createImageBitmap(blob).then(function () { return true; }, function () { return false; })];
            }
        });
    }); },
    add: addFormats('webp'),
    remove: removeFormats('webp')
};

var validExtensions = ['.xml', '.fnt'];
/** simple loader plugin for loading in bitmap fonts! */
var loadBitmapFont = {
    extension: ExtensionType.LoadParser,
    test: function (url) {
        return validExtensions.includes(path.extname(url));
    },
    testParse: function (data) {
        return __awaiter(this, void 0, Promise, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, TextFormat.test(data) || XMLStringFormat.test(data)];
            });
        });
    },
    parse: function (asset, data, loader) {
        return __awaiter(this, void 0, Promise, function () {
            var fontData, src, pages, textureUrls, i, pageFile, imagePath, textures, _a, _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        fontData = TextFormat.test(asset)
                            ? TextFormat.parse(asset)
                            : XMLStringFormat.parse(asset);
                        src = data.src;
                        pages = fontData.page;
                        textureUrls = [];
                        for (i = 0; i < pages.length; ++i) {
                            pageFile = pages[i].file;
                            imagePath = path.join(path.dirname(src), pageFile);
                            textureUrls.push(imagePath);
                        }
                        _b = (_a = Object).values;
                        return [4 /*yield*/, loader.load(textureUrls)];
                    case 1:
                        textures = _b.apply(_a, [_c.sent()]);
                        return [2 /*return*/, BitmapFont.install(fontData, textures, true)];
                }
            });
        });
    },
    load: function (url, _options) {
        return __awaiter(this, void 0, Promise, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, settings.ADAPTER.fetch(url)];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response.text()];
                }
            });
        });
    },
    unload: function (bitmapFont) {
        bitmapFont.destroy();
    }
};

/** simple loader plugin for loading json data */
var loadJson = {
    extension: ExtensionType.LoadParser,
    test: function (url) {
        return (path.extname(url).includes('.json'));
    },
    load: function (url) {
        return __awaiter(this, void 0, Promise, function () {
            var response, json;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, settings.ADAPTER.fetch(url)];
                    case 1:
                        response = _a.sent();
                        return [4 /*yield*/, response.json()];
                    case 2:
                        json = _a.sent();
                        return [2 /*return*/, json];
                }
            });
        });
    },
};

/**
 * Loader plugin that parses sprite sheets!
 * once the JSON has been loaded this checks to see if the JSON is spritesheet data.
 * If it is, we load the spritesheets image and parse the data into PIXI.Spritesheet
 * All textures in the sprite sheet are then added to the cache
 */
var loadSpritesheet = {
    extension: ExtensionType.LoadParser,
    testParse: function (asset, options) {
        return __awaiter(this, void 0, Promise, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, (path.extname(options.src).includes('.json') && !!asset.frames)];
            });
        });
    },
    parse: function (asset, options, loader) {
        var _a, _b;
        return __awaiter(this, void 0, Promise, function () {
            var basePath, imagePath, assets, texture, spritesheet, multiPacks, promises, _i, multiPacks_1, item, itemUrl, res;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        basePath = path.dirname(options.src);
                        if (basePath && basePath.lastIndexOf('/') !== (basePath.length - 1)) {
                            basePath += '/';
                        }
                        imagePath = basePath + asset.meta.image;
                        return [4 /*yield*/, loader.load([imagePath])];
                    case 1:
                        assets = _c.sent();
                        texture = assets[imagePath];
                        spritesheet = new Spritesheet(texture.baseTexture, asset, options.src);
                        return [4 /*yield*/, spritesheet.parse()];
                    case 2:
                        _c.sent();
                        multiPacks = (_a = asset === null || asset === void 0 ? void 0 : asset.meta) === null || _a === void 0 ? void 0 : _a.related_multi_packs;
                        if (!Array.isArray(multiPacks)) { return [3 /*break*/, 4]; }
                        promises = [];
                        for (_i = 0, multiPacks_1 = multiPacks; _i < multiPacks_1.length; _i++) {
                            item = multiPacks_1[_i];
                            if (typeof item !== 'string') {
                                continue;
                            }
                            itemUrl = basePath + item;
                            // Check if the file wasn't already added as multipack
                            if ((_b = options.data) === null || _b === void 0 ? void 0 : _b.ignoreMultiPack) {
                                continue;
                            }
                            promises.push(loader.load({
                                src: itemUrl,
                                data: {
                                    ignoreMultiPack: true,
                                }
                            }));
                        }
                        return [4 /*yield*/, Promise.all(promises)];
                    case 3:
                        res = _c.sent();
                        spritesheet.linkedSheets = res;
                        res.forEach(function (item) {
                            item.linkedSheets = [spritesheet].concat(spritesheet.linkedSheets.filter(function (sp) { return (sp !== item); }));
                        });
                        _c.label = 4;
                    case 4: return [2 /*return*/, spritesheet];
                }
            });
        });
    },
    unload: function (spritesheet) {
        spritesheet.destroy(true);
    },
};

/** Simple loader plugin for loading text data */
var loadTxt = {
    extension: ExtensionType.LoadParser,
    test: function (url) {
        return (path.extname(url).includes('.txt'));
    },
    load: function (url) {
        return __awaiter(this, void 0, Promise, function () {
            var response, txt;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, settings.ADAPTER.fetch(url)];
                    case 1:
                        response = _a.sent();
                        return [4 /*yield*/, response.text()];
                    case 2:
                        txt = _a.sent();
                        return [2 /*return*/, txt];
                }
            });
        });
    },
};

var validWeights = ['normal', 'bold',
    '100', '200', '300', '400', '500', '600', '700', '800', '900' ];
var validFonts = ['woff', 'woff2', 'ttf', 'otf'];
/**
 * Return font face name from a file name
 * Ex.: 'fonts/tital-one.woff' turns into 'Titan One'
 * @param url - File url
 */
function getFontFamilyName(url) {
    var ext = path.extname(url);
    var name = path.basename(url, ext);
    // Replace dashes by white spaces
    var nameWithSpaces = name.replace(/(-|_)/g, ' ');
    // Upper case first character of each word
    var nameTitleCase = nameWithSpaces.toLowerCase()
        .split(' ')
        .map(function (word) { return word.charAt(0).toUpperCase() + word.slice(1); })
        .join(' ');
    return nameTitleCase;
}
/** Web font loader plugin */
var loadWebFont = {
    extension: ExtensionType.LoadParser,
    test: function (url) {
        var tempURL = url.split('?')[0];
        var extension = tempURL.split('.').pop();
        return validFonts.includes(extension);
    },
    load: function (url, options) {
        var _a, _b, _c, _d, _e, _f;
        return __awaiter(this, void 0, Promise, function () {
            var fontFaces, name, weights, data, i, weight, font;
            return __generator(this, function (_g) {
                switch (_g.label) {
                    case 0:
                        // Prevent loading font if navigator is not online
                        if (!window.navigator.onLine) {
                            throw new Error('[loadWebFont] Cannot load font - navigator is offline');
                        }
                        if (!('FontFace' in window)) { return [3 /*break*/, 5]; }
                        fontFaces = [];
                        name = (_b = (_a = options.data) === null || _a === void 0 ? void 0 : _a.family) !== null && _b !== void 0 ? _b : getFontFamilyName(url);
                        weights = (_e = (_d = (_c = options.data) === null || _c === void 0 ? void 0 : _c.weights) === null || _d === void 0 ? void 0 : _d.filter(function (weight) { return validWeights.includes(weight); })) !== null && _e !== void 0 ? _e : ['normal'];
                        data = (_f = options.data) !== null && _f !== void 0 ? _f : {};
                        i = 0;
                        _g.label = 1;
                    case 1:
                        if (!(i < weights.length)) { return [3 /*break*/, 4]; }
                        weight = weights[i];
                        font = new FontFace(name, "url(" + url + ")", __assign(__assign({}, data), { weight: weight }));
                        return [4 /*yield*/, font.load()];
                    case 2:
                        _g.sent();
                        document.fonts.add(font);
                        fontFaces.push(font);
                        _g.label = 3;
                    case 3:
                        i++;
                        return [3 /*break*/, 1];
                    case 4: return [2 /*return*/, fontFaces.length === 1 ? fontFaces[0] : fontFaces];
                    case 5:
                        console.warn('[loadWebFont] FontFace API is not supported. Skipping loading font');
                        return [2 /*return*/, null];
                }
            });
        });
    },
    unload: function (font) {
        (Array.isArray(font) ? font : [font])
            .forEach(function (t) { return document.fonts.delete(t); });
    }
};

function checkExtension(url, extension) {
    var tempURL = url.split('?')[0];
    var ext = path.extname(tempURL).toLowerCase();
    if (Array.isArray(extension)) {
        return extension.includes(ext.toLowerCase());
    }
    return ext.toLowerCase() === extension;
}

function createTexture(base, loader, url) {
    var texture = new Texture(base);
    // make sure to nuke the promise if a texture is destroyed..
    texture.baseTexture.on('dispose', function () {
        delete loader.promiseCache[url];
    });
    return texture;
}

/** Load BASIS textures! */
var loadBasis = {
    extension: ExtensionType.LoadParser,
    test: function (url) {
        return checkExtension(url, '.basis');
    },
    load: function (url, asset, loader) {
        return __awaiter(this, void 0, Promise, function () {
            var response, arrayBuffer, resources, type, format, textures;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, TranscoderWorker.onTranscoderInitialized];
                    case 1:
                        _a.sent();
                        return [4 /*yield*/, settings.ADAPTER.fetch(url)];
                    case 2:
                        response = _a.sent();
                        return [4 /*yield*/, response.arrayBuffer()];
                    case 3:
                        arrayBuffer = _a.sent();
                        return [4 /*yield*/, BasisParser.transcode(arrayBuffer)];
                    case 4:
                        resources = _a.sent();
                        type = BASIS_FORMAT_TO_TYPE[resources.basisFormat];
                        format = resources.basisFormat !== BASIS_FORMATS.cTFRGBA32 ? FORMATS.RGB : FORMATS.RGBA;
                        textures = resources.map(function (resource) {
                            var base = new BaseTexture(resource, __assign({ mipmap: resource instanceof CompressedTextureResource && resource.levels > 1
                                    ? MIPMAP_MODES.ON_MANUAL
                                    : MIPMAP_MODES.OFF, alphaMode: ALPHA_MODES.NO_PREMULTIPLIED_ALPHA, type: type, format: format }, asset.data));
                            return createTexture(base, loader, url);
                        });
                        return [2 /*return*/, textures.length === 1 ? textures[0] : textures];
                }
            });
        });
    },
    unload: function (texture) {
        if (Array.isArray(texture)) {
            texture.forEach(function (t) { return t.destroy(true); });
        }
        else {
            texture.destroy(true);
        }
    }
};

/** Load our DDS textures! */
var loadDDS = {
    extension: ExtensionType.LoadParser,
    test: function (url) {
        return checkExtension(url, '.dds');
    },
    load: function (url, asset, loader) {
        return __awaiter(this, void 0, Promise, function () {
            var response, arrayBuffer, resources, textures;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, settings.ADAPTER.fetch(url)];
                    case 1:
                        response = _a.sent();
                        return [4 /*yield*/, response.arrayBuffer()];
                    case 2:
                        arrayBuffer = _a.sent();
                        resources = parseDDS(arrayBuffer);
                        textures = resources.map(function (resource) {
                            var base = new BaseTexture(resource, __assign({ mipmap: MIPMAP_MODES.OFF, alphaMode: ALPHA_MODES.NO_PREMULTIPLIED_ALPHA, resolution: getResolutionOfUrl(url) }, asset.data));
                            return createTexture(base, loader, url);
                        });
                        return [2 /*return*/, textures.length === 1 ? textures[0] : textures];
                }
            });
        });
    },
    unload: function (texture) {
        if (Array.isArray(texture)) {
            texture.forEach(function (t) { return t.destroy(true); });
        }
        else {
            texture.destroy(true);
        }
    }
};

/** Loads KTX textures! */
var loadKTX = {
    extension: ExtensionType.LoadParser,
    test: function (url) {
        return checkExtension(url, '.ktx');
    },
    load: function (url, asset, loader) {
        return __awaiter(this, void 0, Promise, function () {
            var response, arrayBuffer, _a, compressed, uncompressed, kvData, resources, options, textures;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, settings.ADAPTER.fetch(url)];
                    case 1:
                        response = _b.sent();
                        return [4 /*yield*/, response.arrayBuffer()];
                    case 2:
                        arrayBuffer = _b.sent();
                        _a = parseKTX(url, arrayBuffer), compressed = _a.compressed, uncompressed = _a.uncompressed, kvData = _a.kvData;
                        resources = compressed !== null && compressed !== void 0 ? compressed : uncompressed;
                        options = __assign({ mipmap: MIPMAP_MODES.OFF, alphaMode: ALPHA_MODES.NO_PREMULTIPLIED_ALPHA, resolution: getResolutionOfUrl(url) }, asset.data);
                        textures = resources.map(function (resource) {
                            if (resources === uncompressed) {
                                Object.assign(options, {
                                    type: resource.type,
                                    format: resource.format,
                                });
                            }
                            var base = new BaseTexture(resource, options);
                            base.ktxKeyValueData = kvData;
                            return createTexture(base, loader, url);
                        });
                        return [2 /*return*/, textures.length === 1 ? textures[0] : textures];
                }
            });
        });
    },
    unload: function (texture) {
        if (Array.isArray(texture)) {
            texture.forEach(function (t) { return t.destroy(true); });
        }
        else {
            texture.destroy(true);
        }
    }
};

var UUID = 0;
var MAX_WORKERS;
var workerCode = {
    id: 'loadImageBitmap',
    code: "\n    self.onmessage = function(event) {\n\n        async function loadImageBitmap(url)\n        {\n            const response = await fetch(url);\n            const imageBlob =  await response.blob();\n            const imageBitmap = await createImageBitmap(imageBlob);\n            return imageBitmap;\n        }\n\n        loadImageBitmap(event.data.data[0]).then(imageBitmap => {\n            self.postMessage({\n                data: imageBitmap,\n                uuid: event.data.uuid,\n                id: event.data.id,\n            }, [imageBitmap]);\n        }).catch(error => {\n            self.postMessage({\n                data: null,\n                uuid: event.data.uuid,\n                id: event.data.id,\n            });\n        });\n    }",
};
var workerURL;
var WorkerManagerClass = /** @class */ (function () {
    function WorkerManagerClass() {
        this._initialized = false;
        this._createdWorkers = 0;
        this.workerPool = [];
        this.queue = [];
        this.resolveHash = {};
    }
    WorkerManagerClass.prototype.loadImageBitmap = function (src) {
        return this._run('loadImageBitmap', [src]);
    };
    WorkerManagerClass.prototype._initWorkers = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                if (this._initialized)
                    { return [2 /*return*/]; }
                this._initialized = true;
                return [2 /*return*/];
            });
        });
    };
    WorkerManagerClass.prototype.getWorker = function () {
        var _this = this;
        if (MAX_WORKERS === undefined) {
            MAX_WORKERS = navigator.hardwareConcurrency || 4;
        }
        var worker = this.workerPool.pop();
        if (!worker && this._createdWorkers < MAX_WORKERS) {
            if (!workerURL) {
                workerURL = URL.createObjectURL(new Blob([workerCode.code], { type: 'application/javascript' }));
            }
            // only create as many as MAX_WORKERS allows..
            this._createdWorkers++;
            worker = new Worker(workerURL);
            worker.addEventListener('message', function (event) {
                _this.complete(event.data);
                _this.returnWorker(event.target);
                _this.next();
            });
        }
        return worker;
    };
    WorkerManagerClass.prototype.returnWorker = function (worker) {
        this.workerPool.push(worker);
    };
    WorkerManagerClass.prototype.complete = function (data) {
        var result = data.data;
        this.resolveHash[data.uuid](result);
        this.resolveHash[data.uuid] = null;
    };
    WorkerManagerClass.prototype._run = function (id, args) {
        var _this = this;
        this._initWorkers();
        // push into the queue...
        var promise = new Promise(function (resolve) {
            _this.queue.push({ id: id, arguments: args, resolve: resolve });
        });
        this.next();
        return promise;
    };
    WorkerManagerClass.prototype.next = function () {
        // nothing to do
        if (!this.queue.length)
            { return; }
        var worker = this.getWorker();
        // no workers available...
        if (!worker) {
            return;
        }
        var toDo = this.queue.pop();
        var id = toDo.id;
        this.resolveHash[UUID] = toDo.resolve;
        worker.postMessage({
            data: toDo.arguments,
            uuid: UUID++,
            id: id,
        });
    };
    return WorkerManagerClass;
}());
var WorkerManager = new WorkerManagerClass();

var validImages$1 = ['.jpg', '.png', '.jpeg', '.avif', '.webp'];
/**
 * Returns a promise that resolves an ImageBitmaps.
 * This function is designed to be used by a worker.
 * Part of WorkerManager!
 * @param url - The image to load an image bitmap for
 */
function loadImageBitmap(url) {
    return __awaiter(this, void 0, Promise, function () {
        var response, imageBlob, imageBitmap;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, settings.ADAPTER.fetch(url)];
                case 1:
                    response = _a.sent();
                    return [4 /*yield*/, response.blob()];
                case 2:
                    imageBlob = _a.sent();
                    return [4 /*yield*/, createImageBitmap(imageBlob)];
                case 3:
                    imageBitmap = _a.sent();
                    return [2 /*return*/, imageBitmap];
            }
        });
    });
}
/**
 * Loads our textures!
 * this makes use of imageBitmaps where available.
 * We load the ImageBitmap on a different thread using the WorkerManager
 * We can then use the ImageBitmap as a source for a Pixi Texture
 */
var loadTextures = {
    extension: ExtensionType.LoadParser,
    config: {
        preferWorkers: true,
    },
    test: function (url) {
        var isValidBase64Suffix = false;
        for (var i = 0; i < validImages$1.length; i++) {
            if (url.indexOf("data:image/" + validImages$1[i].slice(1)) === 0) {
                isValidBase64Suffix = true;
                break;
            }
        }
        return isValidBase64Suffix || checkExtension(url, validImages$1);
    },
    load: function (url, asset, loader) {
        return __awaiter(this, void 0, Promise, function () {
            var src, _a, base;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        src = null;
                        if (!window.createImageBitmap) { return [3 /*break*/, 5]; }
                        if (!this.config.preferWorkers) { return [3 /*break*/, 2]; }
                        return [4 /*yield*/, WorkerManager.loadImageBitmap(url)];
                    case 1:
                        _a = _b.sent();
                        return [3 /*break*/, 4];
                    case 2: return [4 /*yield*/, loadImageBitmap(url)];
                    case 3:
                        _a = _b.sent();
                        _b.label = 4;
                    case 4:
                        src = _a;
                        return [3 /*break*/, 7];
                    case 5: return [4 /*yield*/, new Promise(function (resolve) {
                            src = new Image();
                            src.crossOrigin = 'anonymous';
                            src.src = url;
                            if (src.complete) {
                                resolve(src);
                            }
                            else {
                                src.onload = function () {
                                    resolve(src);
                                };
                            }
                        })];
                    case 6:
                        src = _b.sent();
                        _b.label = 7;
                    case 7:
                        base = new BaseTexture(src, __assign({ resolution: getResolutionOfUrl(url) }, asset.data));
                        base.resource.src = url;
                        return [2 /*return*/, createTexture(base, loader, url)];
                }
            });
        });
    },
    unload: function (texture) {
        texture.destroy(true);
    }
};

/** Loads SVG's into Textures */
var loadSVG = {
    extension: ExtensionType.LoadParser,
    test: function (url) {
        return (path.extname(url).includes('.svg'));
    },
    testParse: function (data) {
        return __awaiter(this, void 0, Promise, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, SVGResource.test(data)];
            });
        });
    },
    parse: function (asset, data, loader) {
        var _a, _b, _c;
        return __awaiter(this, void 0, Promise, function () {
            var src, base, texture;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        src = new SVGResource(asset, (_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.resourceOptions);
                        base = new BaseTexture(src, __assign({ resolution: getResolutionOfUrl(asset) }, data === null || data === void 0 ? void 0 : data.data));
                        base.resource.src = asset;
                        texture = createTexture(base, loader, asset);
                        if (!!((_c = (_b = data === null || data === void 0 ? void 0 : data.data) === null || _b === void 0 ? void 0 : _b.resourceOptions) === null || _c === void 0 ? void 0 : _c.autoLoad)) { return [3 /*break*/, 2]; }
                        return [4 /*yield*/, src.load()];
                    case 1:
                        _d.sent();
                        _d.label = 2;
                    case 2: return [2 /*return*/, texture];
                }
            });
        });
    },
    load: function (url, _options) {
        return __awaiter(this, void 0, Promise, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, settings.ADAPTER.fetch(url)];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response.text()];
                }
            });
        });
    },
    unload: loadTextures.unload,
};

/**
 * The Loader is responsible for loading all assets, such as images, spritesheets, audio files, etc.
 * It does not do anything clever with URLs - it just loads stuff!
 * Behind the scenes all things are cached using promises. This means it's impossible to load an asset more than once.
 * Through the use of LoaderParsers, the loader can understand how to load any kind of file!
 *
 * It is not intended that this class is created by developers - its part of the Asset class
 * This is the second major system of PixiJS' main Assets class
 * @memberof PIXI
 * @class AssetLoader
 */
var Loader = /** @class */ (function () {
    function Loader() {
        this._parsers = [];
        /** Cache loading promises that ae currently active */
        this.promiseCache = {};
    }
    /** function used for testing */
    Loader.prototype.reset = function () {
        this.promiseCache = {};
    };
    /**
     * Used internally to generate a promise for the asset to be loaded.
     * @param url - The URL to be loaded
     * @param data - any custom additional information relevant to the asset being loaded
     * @returns - a promise that will resolve to an Asset for example a Texture of a JSON object
     */
    Loader.prototype._getLoadPromiseAndParser = function (url, data) {
        var _this = this;
        var result = {
            promise: null,
            parser: null
        };
        result.promise = (function () { return __awaiter(_this, void 0, void 0, function () {
            var asset, i, parser, i, parser, _a;
            var _b, _c;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        asset = null;
                        i = 0;
                        _d.label = 1;
                    case 1:
                        if (!(i < this.parsers.length)) { return [3 /*break*/, 4]; }
                        parser = this.parsers[i];
                        if (!(parser.load && ((_b = parser.test) === null || _b === void 0 ? void 0 : _b.call(parser, url, data, this)))) { return [3 /*break*/, 3]; }
                        return [4 /*yield*/, parser.load(url, data, this)];
                    case 2:
                        asset = _d.sent();
                        result.parser = parser;
                        return [3 /*break*/, 4];
                    case 3:
                        i++;
                        return [3 /*break*/, 1];
                    case 4:
                        if (!result.parser) {
                            // eslint-disable-next-line max-len
                            console.warn("[Assets] " + url + " could not be loaded as we don't know how to parse it, ensure the correct parser has being added");
                            return [2 /*return*/, null];
                        }
                        i = 0;
                        _d.label = 5;
                    case 5:
                        if (!(i < this.parsers.length)) { return [3 /*break*/, 10]; }
                        parser = this.parsers[i];
                        if (!parser.parse) { return [3 /*break*/, 9]; }
                        _a = parser.parse;
                        if (!_a) { return [3 /*break*/, 7]; }
                        return [4 /*yield*/, ((_c = parser.testParse) === null || _c === void 0 ? void 0 : _c.call(parser, asset, data, this))];
                    case 6:
                        _a = (_d.sent());
                        _d.label = 7;
                    case 7:
                        if (!_a) { return [3 /*break*/, 9]; }
                        return [4 /*yield*/, parser.parse(asset, data, this)];
                    case 8:
                        // transform the asset..
                        asset = (_d.sent()) || asset;
                        result.parser = parser;
                        _d.label = 9;
                    case 9:
                        i++;
                        return [3 /*break*/, 5];
                    case 10: return [2 /*return*/, asset];
                }
            });
        }); })();
        return result;
    };
    /**
     * Loads an asset(s) using the parsers added to the Loader.
     * @example
     * // single asset:
     * const asset = await Loader.load('cool.png');
     * console.log(asset);
     * @example
     * // multiple assets:
     * const assets = await  Loader.load(['cool.png', 'cooler.png']);
     * console.log(assets);
     * @param assetsToLoadIn - urls that you want to load, or a single one!
     * @param onProgress - a function that gets called when the progress changes
     */
    Loader.prototype.load = function (assetsToLoadIn, onProgress) {
        return __awaiter(this, void 0, Promise, function () {
            var count, assets, singleAsset, assetsToLoad, total, promises;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        count = 0;
                        assets = {};
                        singleAsset = isSingleItem(assetsToLoadIn);
                        assetsToLoad = convertToList(assetsToLoadIn, function (item) { return ({
                            src: item,
                        }); });
                        total = assetsToLoad.length;
                        promises = assetsToLoad.map(function (asset) { return __awaiter(_this, void 0, void 0, function () {
                            var url, _a, _b, e_1;
                            return __generator(this, function (_c) {
                                switch (_c.label) {
                                    case 0:
                                        url = path.toAbsolute(asset.src);
                                        if (!!assets[asset.src]) { return [3 /*break*/, 4]; }
                                        _c.label = 1;
                                    case 1:
                                        _c.trys.push([1, 3, , 4]);
                                        if (!this.promiseCache[url]) {
                                            this.promiseCache[url] = this._getLoadPromiseAndParser(url, asset);
                                        }
                                        _a = assets;
                                        _b = asset.src;
                                        return [4 /*yield*/, this.promiseCache[url].promise];
                                    case 2:
                                        _a[_b] = _c.sent();
                                        // Only progress if nothing goes wrong
                                        if (onProgress)
                                            { onProgress(++count / total); }
                                        return [3 /*break*/, 4];
                                    case 3:
                                        e_1 = _c.sent();
                                        // Delete eventually registered file and promises from internal cache
                                        // so they can be eligible for another loading attempt
                                        delete this.promiseCache[url];
                                        delete assets[asset.src];
                                        // Stop further execution
                                        throw new Error("[Loader.load] Failed to load " + url + ".\n" + e_1);
                                    case 4: return [2 /*return*/];
                                }
                            });
                        }); });
                        return [4 /*yield*/, Promise.all(promises)];
                    case 1:
                        _a.sent();
                        return [2 /*return*/, singleAsset ? assets[assetsToLoad[0].src] : assets];
                }
            });
        });
    };
    /**
     * Unloads an asset(s). Any unloaded assets will be destroyed, freeing up memory for your app.
     * The parser that created the asset, will be the one that unloads it.
     * @example
     * // single asset:
     * const asset = await Loader.load('cool.png');
     *
     * await Loader.unload('cool.png');
     *
     * console.log(asset.destroyed); // true
     * @param assetsToUnloadIn - urls that you want to unload, or a single one!
     */
    Loader.prototype.unload = function (assetsToUnloadIn) {
        return __awaiter(this, void 0, Promise, function () {
            var assetsToUnload, promises;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        assetsToUnload = convertToList(assetsToUnloadIn, function (item) { return ({
                            src: item,
                        }); });
                        promises = assetsToUnload.map(function (asset) { return __awaiter(_this, void 0, void 0, function () {
                            var url, loadPromise, loadedAsset;
                            var _a, _b;
                            return __generator(this, function (_c) {
                                switch (_c.label) {
                                    case 0:
                                        url = path.toAbsolute(asset.src);
                                        loadPromise = this.promiseCache[url];
                                        if (!loadPromise) { return [3 /*break*/, 2]; }
                                        return [4 /*yield*/, loadPromise.promise];
                                    case 1:
                                        loadedAsset = _c.sent();
                                        (_b = (_a = loadPromise.parser) === null || _a === void 0 ? void 0 : _a.unload) === null || _b === void 0 ? void 0 : _b.call(_a, loadedAsset, asset, this);
                                        delete this.promiseCache[url];
                                        _c.label = 2;
                                    case 2: return [2 /*return*/];
                                }
                            });
                        }); });
                        return [4 /*yield*/, Promise.all(promises)];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    Object.defineProperty(Loader.prototype, "parsers", {
        /** All loader parsers registered */
        get: function () {
            return this._parsers;
        },
        enumerable: false,
        configurable: true
    });
    return Loader;
}());

var validImages = ['jpg', 'png', 'jpeg', 'avif', 'webp'];
var resolveSpriteSheetUrl = {
    extension: ExtensionType.ResolveParser,
    test: function (value) {
        var tempURL = value.split('?')[0];
        var split = tempURL.split('.');
        var extension = split.pop();
        var format = split.pop();
        return extension === 'json' && validImages.includes(format);
    },
    parse: function (value) {
        var _a, _b;
        var split = value.split('.');
        return {
            resolution: parseFloat((_b = (_a = settings.RETINA_PREFIX.exec(value)) === null || _a === void 0 ? void 0 : _a[1]) !== null && _b !== void 0 ? _b : '1'),
            format: split[split.length - 2],
            src: value,
        };
    },
};

var resolveTextureUrl = {
    extension: ExtensionType.ResolveParser,
    test: loadTextures.test,
    parse: function (value) {
        var _a, _b;
        return ({
            resolution: parseFloat((_b = (_a = settings.RETINA_PREFIX.exec(value)) === null || _a === void 0 ? void 0 : _a[1]) !== null && _b !== void 0 ? _b : '1'),
            format: value.split('.').pop(),
            src: value,
        });
    },
};

var resolveCompressedTextureUrl = {
    extension: ExtensionType.ResolveParser,
    test: function (value) {
        var temp = value.split('?')[0];
        var extension = temp.split('.').pop();
        return ['basis', 'ktx', 'dds'].includes(extension);
    },
    parse: function (value) {
        var _a, _b, _c, _d;
        var temp = value.split('?')[0];
        var extension = temp.split('.').pop();
        if (extension === 'ktx') {
            var extensions = [
                '.s3tc.ktx',
                '.s3tc_sRGB.ktx',
                '.etc.ktx',
                '.etc1.ktx',
                '.pvrt.ktx',
                '.atc.ktx',
                '.astc.ktx'
            ];
            // check if value ends with one of the extensions
            if (extensions.some(function (ext) { return value.endsWith(ext); })) {
                return {
                    resolution: parseFloat((_b = (_a = settings.RETINA_PREFIX.exec(value)) === null || _a === void 0 ? void 0 : _a[1]) !== null && _b !== void 0 ? _b : '1'),
                    format: extensions.find(function (ext) { return value.endsWith(ext); }),
                    src: value,
                };
            }
        }
        return {
            resolution: parseFloat((_d = (_c = settings.RETINA_PREFIX.exec(value)) === null || _c === void 0 ? void 0 : _c[1]) !== null && _d !== void 0 ? _d : '1'),
            format: value.split('.').pop(),
            src: value,
        };
    },
};

/**
 * A class that is responsible for resolving mapping asset URLs to keys.
 * At its most basic it can be used for Aliases:
 *
 * ```
 * resolver.add('foo', 'bar');
 * resolver.resolveUrl('foo') // => 'bar'
 * ```
 *
 * It can also be used to resolve the most appropriate asset for a given URL:
 *
 * ```
 *  resolver.prefer({
 *      params:{
 *          format:'webp',
 *          resolution: 2,
 *      }
 *  })
 *
 *  resolver.add('foo', ['bar@2x.webp', 'bar@2x.png', 'bar.webp', 'bar.png']);
 *
 *  resolver.resolveUrl('foo') // => 'bar@2x.webp'
 * ```
 * Other features include:
 * - Ability to process a manifest file to get the correct understanding of how to resolve all assets
 * - Ability to add custom parsers for specific file types
 * - Ability to add custom prefer rules
 *
 * This class only cares about the URL, not the loading of the asset itself.
 *
 * It is not intended that this class is created by developers - its part of the Asset class
 * This is the third major system of PixiJS' main Assets class
 * @memberof PIXI
 */
var Resolver = /** @class */ (function () {
    function Resolver() {
        this._assetMap = {};
        this._preferredOrder = [];
        this._parsers = [];
        this._resolverHash = {};
        this._bundles = {};
    }
    /**
     * Let the resolver know which assets you prefer to use when resolving assets.
     * Multiple prefer user defined rules can be added.
     * @example
     * resolver.prefer({
     *     // first look for something with the correct format, and then then correct resolution
     *     priority: ['format', 'resolution'],
     *     params:{
     *         format:'webp', // prefer webp images
     *         resolution: 2, // prefer a resolution of 2
     *     }
     * })
     * resolver.add('foo', ['bar@2x.webp', 'bar@2x.png', 'bar.webp', 'bar.png']);
     * resolver.resolveUrl('foo') // => 'bar@2x.webp'
     * @param preferOrders - the prefer options
     */
    Resolver.prototype.prefer = function () {
        var arguments$1 = arguments;

        var _this = this;
        var preferOrders = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            preferOrders[_i] = arguments$1[_i];
        }
        preferOrders.forEach(function (prefer) {
            _this._preferredOrder.push(prefer);
            if (!prefer.priority) {
                // generate the priority based on the order of the object
                prefer.priority = Object.keys(prefer.params);
            }
        });
        this._resolverHash = {};
    };
    Object.defineProperty(Resolver.prototype, "basePath", {
        get: function () {
            return this._basePath;
        },
        /**
         * Set the base path to prepend to all urls when resolving
         * @example
         * resolver.basePath = 'https://home.com/';
         * resolver.add('foo', 'bar.ong');
         * resolver.resolveUrl('foo', 'bar.png'); // => 'https://home.com/bar.png'
         * @param basePath - the base path to use
         */
        set: function (basePath) {
            this._basePath = basePath;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Resolver.prototype, "rootPath", {
        get: function () {
            return this._rootPath;
        },
        /**
         * Set the root path for root-relative URLs. By default the `basePath`'s root is used. If no `basePath` is set, then the
         * default value for browsers is `window.location.origin`
         * @example
         * // Application hosted on https://home.com/some-path/index.html
         * resolver.basePath = 'https://home.com/some-path/';
         * resolver.rootPath = 'https://home.com/';
         * resolver.add('foo', '/bar.png');
         * resolver.resolveUrl('foo', '/bar.png'); // => 'https://home.com/bar.png'
         * @param rootPath - the root path to use
         */
        set: function (rootPath) {
            this._rootPath = rootPath;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Resolver.prototype, "parsers", {
        /**
         * All the active URL parsers that help the parser to extract information and create
         * an asset object-based on parsing the URL itself.
         *
         * Can be added using the extensions API
         * @example
         * resolver.add('foo', [
         *    {
         *      resolution:2,
         *      format:'png'
         *      src: 'image@2x.png'
         *    },
         *    {
         *      resolution:1,
         *      format:'png'
         *      src: 'image.png'
         *    }
         * ]);
         *
         * // with a url parser the information such as resolution and file format could extracted from the url itself:
         * extensions.add({
         *     extension: ExtensionType.ResolveParser,
         *     test: loadTextures.test, // test if url ends in an image
         *     parse: (value: string) =>
         *     ({
         *         resolution: parseFloat(settings.RETINA_PREFIX.exec(value)?.[1] ?? '1'),
         *         format: value.split('.').pop(),
         *         src: value,
         *     }),
         * });
         *
         * // now resolution and format can be extracted from the url
         * resolver.add('foo', [
         *    'image@2x.png'
         *    'image.png'
         * ]);
         * @
         */
        get: function () {
            return this._parsers;
        },
        enumerable: false,
        configurable: true
    });
    /** Used for testing, this resets the resolver to its initial state */
    Resolver.prototype.reset = function () {
        this._preferredOrder = [];
        this._resolverHash = {};
        this._assetMap = {};
        this._rootPath = null;
        this._basePath = null;
        this._manifest = null;
    };
    /**
     * Add a manifest to the asset resolver. This is a nice way to add all the asset information in one go.
     * generally a manifest would be built using a tool.
     * @param manifest - the manifest to add to the resolver
     */
    Resolver.prototype.addManifest = function (manifest) {
        var _this = this;
        if (this._manifest) {
            console.warn('[Resolver] Manifest already exists, this will be overwritten');
        }
        this._manifest = manifest;
        manifest.bundles.forEach(function (bundle) {
            _this.addBundle(bundle.name, bundle.assets);
        });
    };
    /**
     * This adds a bundle of assets in one go so that you can resolve them as a group.
     * For example you could add a bundle for each screen in you pixi app
     * @example
     *  resolver.addBundle('animals', {
     *    bunny: 'bunny.png',
     *    chicken: 'chicken.png',
     *    thumper: 'thumper.png',
     *  });
     *
     * const resolvedAssets = await resolver.resolveBundle('animals');
     * @param bundleId - The id of the bundle to add
     * @param assets - A record of the asset or assets that will be chosen from when loading via the specified key
     */
    Resolver.prototype.addBundle = function (bundleId, assets) {
        var _this = this;
        var assetNames = [];
        if (Array.isArray(assets)) {
            assets.forEach(function (asset) {
                if (typeof asset.name === 'string') {
                    assetNames.push(asset.name);
                }
                else {
                    assetNames.push.apply(assetNames, asset.name);
                }
                _this.add(asset.name, asset.srcs);
            });
        }
        else {
            Object.keys(assets).forEach(function (key) {
                assetNames.push(key);
                _this.add(key, assets[key]);
            });
        }
        this._bundles[bundleId] = assetNames;
    };
    /**
     * Tells the resolver what keys are associated with witch asset.
     * The most important thing the resolver does
     * @example
     * // single key, single asset:
     * resolver.add('foo', 'bar.png');
     * resolver.resolveUrl('foo') // => 'bar.png'
     *
     * // multiple keys, single asset:
     * resolver.add(['foo', 'boo'], 'bar.png');
     * resolver.resolveUrl('foo') // => 'bar.png'
     * resolver.resolveUrl('boo') // => 'bar.png'
     *
     * // multiple keys, multiple assets:
     * resolver.add(['foo', 'boo'], ['bar.png', 'bar.webp']);
     * resolver.resolveUrl('foo') // => 'bar.png'
     *
     * // add custom data attached to the resolver
     * Resolver.add(
     *     'bunnyBooBooSmooth',
     *     'bunny{png,webp}',
     *     {scaleMode:SCALE_MODES.NEAREST} // base texture options
     * );
     *
     * resolver.resolve('bunnyBooBooSmooth') // => {src: 'bunny.png', data: {scaleMode: SCALE_MODES.NEAREST}}
     * @param keysIn - The keys to map, can be an array or a single key
     * @param assetsIn - The assets to associate with the key(s)
     * @param data - The data that will be attached to the object that resolved object.
     */
    Resolver.prototype.add = function (keysIn, assetsIn, data) {
        var _this = this;
        var keys = convertToList(keysIn);
        keys.forEach(function (key) {
            if (_this._assetMap[key]) {
                console.warn("[Resolver] already has key: " + key + " overwriting");
            }
        });
        if (!Array.isArray(assetsIn)) {
            if (typeof assetsIn === 'string') {
                assetsIn = createStringVariations(assetsIn);
            }
            else {
                assetsIn = [assetsIn];
            }
        }
        var assetMap = assetsIn.map(function (asset) {
            var _a;
            var formattedAsset = asset;
            // check if is a string
            if (typeof asset === 'string') {
                // first see if it contains any {} tags...
                var parsed = false;
                for (var i = 0; i < _this._parsers.length; i++) {
                    var parser = _this._parsers[i];
                    if (parser.test(asset)) {
                        formattedAsset = parser.parse(asset);
                        parsed = true;
                        break;
                    }
                }
                if (!parsed) {
                    formattedAsset = {
                        src: asset,
                    };
                }
            }
            if (!formattedAsset.format) {
                formattedAsset.format = formattedAsset.src.split('.').pop();
            }
            if (!formattedAsset.alias) {
                formattedAsset.alias = keys;
            }
            if (_this._basePath || _this._rootPath) {
                formattedAsset.src = path.toAbsolute(formattedAsset.src, _this._basePath, _this._rootPath);
            }
            formattedAsset.data = (_a = formattedAsset.data) !== null && _a !== void 0 ? _a : data;
            return formattedAsset;
        });
        keys.forEach(function (key) {
            _this._assetMap[key] = assetMap;
        });
    };
    /**
     * If the resolver has had a manifest set via setManifest, this will return the assets urls for
     * a given bundleId or bundleIds.
     * @example
     * // manifest example
     * const manifest = {
     *   bundles:[{
     *      name:'load-screen',
     *      assets:[
     *          {
     *             name: 'background',
     *             srcs: 'sunset.png',
     *          },
     *          {
     *             name: 'bar',
     *             srcs: 'load-bar.{png,webp}',
     *          }
     *      ]
     *   },
     *   {
     *      name:'game-screen',
     *      assets:[
     *          {
     *             name: 'character',
     *             srcs: 'robot.png',
     *          },
     *          {
     *             name: 'enemy',
     *             srcs: 'bad-guy.png',
     *          }
     *      ]
     *   }]
     * }}
     * resolver.setManifest(manifest);
     * const resolved = resolver.resolveBundle('load-screen');
     * @param bundleIds - The bundle ids to resolve
     * @returns All the bundles assets or a hash of assets for each bundle specified
     */
    Resolver.prototype.resolveBundle = function (bundleIds) {
        var _this = this;
        var singleAsset = isSingleItem(bundleIds);
        bundleIds = convertToList(bundleIds);
        var out = {};
        bundleIds.forEach(function (bundleId) {
            var assetNames = _this._bundles[bundleId];
            if (assetNames) {
                out[bundleId] = _this.resolve(assetNames);
            }
        });
        return singleAsset ? out[bundleIds[0]] : out;
    };
    /**
     * Does exactly what resolve does, but returns just the URL rather than the whole asset object
     * @param key - The key or keys to resolve
     * @returns - The URLs associated with the key(s)
     */
    Resolver.prototype.resolveUrl = function (key) {
        var result = this.resolve(key);
        if (typeof key !== 'string') {
            var out = {};
            for (var i in result) {
                out[i] = result[i].src;
            }
            return out;
        }
        return result.src;
    };
    /**
     * Resolves each key in the list to an asset object.
     * Another key function of the resolver! After adding all the various key/asset pairs. this will run the logic
     * of finding which asset to return based on any preferences set using the `prefer` function
     * by default the same key passed in will be returned if nothing is matched by the resolver.
     * @example
     * resolver.add('boo', 'bunny.png');
     *
     * resolver.resolve('boo') // => {src:'bunny.png'}
     *
     * // will return the same string as no key was added for this value..
     * resolver.resolve('another-thing.png') // => {src:'another-thing.png'}
     * @param keys - key or keys to resolve
     * @returns - the resolve asset or a hash of resolve assets for each key specified
     */
    Resolver.prototype.resolve = function (keys) {
        var _this = this;
        var singleAsset = isSingleItem(keys);
        keys = convertToList(keys);
        var result = {};
        keys.forEach(function (key) {
            var _a;
            if (!_this._resolverHash[key]) {
                if (_this._assetMap[key]) {
                    var assets_1 = _this._assetMap[key];
                    var preferredOrder_1 = _this._getPreferredOrder(assets_1);
                    var bestAsset = assets_1[0];
                    preferredOrder_1 === null || preferredOrder_1 === void 0 ? void 0 : preferredOrder_1.priority.forEach(function (priorityKey) {
                        preferredOrder_1.params[priorityKey].forEach(function (value) {
                            var filteredAssets = assets_1.filter(function (asset) {
                                if (asset[priorityKey]) {
                                    return asset[priorityKey] === value;
                                }
                                return false;
                            });
                            if (filteredAssets.length) {
                                assets_1 = filteredAssets;
                            }
                        });
                    });
                    _this._resolverHash[key] = ((_a = assets_1[0]) !== null && _a !== void 0 ? _a : bestAsset);
                }
                else {
                    var src = key;
                    if (_this._basePath || _this._rootPath) {
                        src = path.toAbsolute(src, _this._basePath, _this._rootPath);
                    }
                    // if the resolver fails we just pass back the key assuming its a url
                    _this._resolverHash[key] = {
                        src: src,
                    };
                }
            }
            result[key] = _this._resolverHash[key];
        });
        return singleAsset ? result[keys[0]] : result;
    };
    /**
     * Internal function for figuring out what prefer criteria an asset should use.
     * @param assets
     */
    Resolver.prototype._getPreferredOrder = function (assets) {
        var _loop_1 = function (i) {
            var asset = assets[0];
            var preferred = this_1._preferredOrder.find(function (preference) {
                return preference.params.format.includes(asset.format);
            });
            if (preferred) {
                return { value: preferred };
            }
        };
        var this_1 = this;
        for (var i = 0; i < assets.length; i++) {
            var state_1 = _loop_1();
            if (typeof state_1 === "object")
                { return state_1.value; }
        }
        return this._preferredOrder[0];
    };
    return Resolver;
}());

/**
 * A one stop shop for all Pixi resource management!
 * Super modern and easy to use, with enough flexibility to customize and do what you need!
 * @memberof PIXI
 * @namespace Assets
 *
 * Only one Asset Class exists accessed via the Global Asset object.
 *
 * It has four main responsibilities:
 * 1. Allows users to map URLs to keys and resolve them according to the user's browser capabilities
 * 2. Loads the resources and transforms them into assets that developers understand.
 * 3. Caches the assets and provides a way to access them.
 * 4: Allow developers to unload assets and clear the cache.
 *
 * It also has a few advanced features:
 * 1. Allows developers to provide a manifest upfront of all assets and help manage them via 'bundles'
 * 2. Allows users to background load assets. Shortening (or eliminating) load times and improving UX. With this feature,
 * in-game loading bars can be a thing of the past!
 *
 *
 * Do not be afraid to load things multiple times - under the hood, it will NEVER load anything more than once.
 *
 * for example:
 *
 * ```
 * promise1 = PIXI.Assets.load('bunny.png')
 * promise2 = PIXI.Assets.load('bunny.png')
 *
 * //promise1 === promise2
 * ```
 * here both promises will be the same. Once resolved.. forever resolved! It makes for really easy resource management!
 *
 * Out of the box it supports the following files:
 * * textures (avif, webp, png, jpg, gif)
 * * sprite sheets (json)
 * * bitmap fonts (xml, fnt, txt)
 * * web fonts (ttf, woff, woff2)
 * * json files (json)
 * * text files (txt)
 *
 * More types can be added fairly easily by creating additional loader parsers.
 *
 * ### Textures
 * - Textures are loaded as ImageBitmap on a worker thread where possible.
 * Leading to much less janky load + parse times.
 * - By default, we will prefer to load AVIF and WebP image files if you specify them.
 * But if the browser doesn't support AVIF or WebP we will fall back to png and jpg.
 * - Textures can also be accessed via Texture.from(...) and now use this asset manager under the hood!
 * - Don't worry if you set preferences for textures that don't exist (for example you prefer 2x resolutions images
 *  but only 1x is available for that texture, the Asset manager will pick that up as a fallback automatically)
 * #### Sprite sheets
 * - it's hard to know what resolution a sprite sheet is without loading it first, to address this
 * there is a naming convention we have added that will let Pixi understand the image format and resolution
 * of the spritesheet via its file name:
 *
 * `my-spritesheet{resolution}.{imageFormat}.json`
 *
 * for example:
 *
 * `my-spritesheet@2x.webp.json` // 2x resolution, WebP sprite sheet
 * `my-spritesheet@0.5x.png.json` // 0.5x resolution, png sprite sheet
 *
 * This is optional! you can just load a sprite sheet as normal,
 * This is only useful if you have a bunch of different res / formatted spritesheets
 *
 * ### Fonts
 * * Web fonts will be loaded with all weights.
 * it is possible to load only specific weights by doing the following:
 *
 * ```
 * // load specific weights..
 * await PIXI.Assets.load({
 *    data: {
 *      weights: ['normal'], // only loads the weight
 *    },
 *    src: `outfit.woff2`,
 * });
 *
 * // load everything...
 * await PIXI.Assets.load(`outfit.woff2`);
 * ```
 * ### Background Loading
 * Background loading will load stuff for you passively behind the scenes. To minimize jank,
 * it will only load one asset at a time. As soon as a developer calls `PIXI.Assets.load(...)` the
 * background loader is paused and requested assets are loaded as a priority.
 * Don't worry if something is in there that's already loaded, it will just get skipped!
 *
 * You still need to call `PIXI.Assets.load(...)` to get an asset that has been loaded in the background.
 * It's just that this promise will resolve instantly if the asset
 * has already been loaded.
 *
 * ### Manifest and Bundles
 * - Manifest is a JSON file that contains a list of all assets and their properties.
 * - Bundles are a way to group assets together.
 *
 * ```
 * // manifest example
 * const manifest = {
 *   bundles:[{
 *      name:'load-screen',
 *      assets:[
 *          {
 *             name: 'background',
 *             srcs: 'sunset.png',
 *          },
 *          {
 *             name: 'bar',
 *             srcs: 'load-bar.{png,webp}',
 *          }
 *      ]
 *   },
 *   {
 *      name:'game-screen',
 *      assets:[
 *          {
 *             name: 'character',
 *             srcs: 'robot.png',
 *          },
 *          {
 *             name: 'enemy',
 *             srcs: 'bad-guy.png',
 *          }
 *      ]
 *   }]
 * }}
 *
 * await PIXI.Asset.init({
 *  manifest
 * });
 *
 * // load a bundle..
 * loadScreenAssets = await PIXI.Assets.loadBundle('load-screen');
 * // load another..
 * gameScreenAssets = await PIXI.Assets.loadBundle('game-screen');
 * ```
 * @example
 * const bunny = await PIXI.Assets.load('bunny.png');
 */
var AssetsClass = /** @class */ (function () {
    function AssetsClass() {
        this._detections = [];
        this._initialized = false;
        this.resolver = new Resolver();
        this.loader = new Loader();
        this.cache = Cache;
        this._backgroundLoader = new BackgroundLoader(this.loader);
        this._backgroundLoader.active = true;
        this.reset();
    }
    /**
     * Best practice is to call this function before any loading commences
     * Initiating is the best time to add any customization to the way things are loaded.
     *
     * you do not need to call this for the Asset class to work, only if you want to set any initial properties
     * @param options - options to initialize the Asset manager with
     */
    AssetsClass.prototype.init = function (options) {
        var _a, _b, _c, _d;
        if (options === void 0) { options = {}; }
        return __awaiter(this, void 0, Promise, function () {
            var manifest, resolutionPref, resolution, formats, formatPref, _i, _e, detection, _f, _g, detection;
            return __generator(this, function (_h) {
                switch (_h.label) {
                    case 0:
                        if (this._initialized) {
                            console.warn('[Assets]AssetManager already initialized, did you load before calling this Asset.init()?');
                            return [2 /*return*/];
                        }
                        this._initialized = true;
                        if (options.basePath) {
                            this.resolver.basePath = options.basePath;
                        }
                        if (!options.manifest) { return [3 /*break*/, 3]; }
                        manifest = options.manifest;
                        if (!(typeof manifest === 'string')) { return [3 /*break*/, 2]; }
                        return [4 /*yield*/, this.load(manifest)];
                    case 1:
                        manifest = (_h.sent());
                        _h.label = 2;
                    case 2:
                        this.resolver.addManifest(manifest);
                        _h.label = 3;
                    case 3:
                        resolutionPref = (_b = (_a = options.texturePreference) === null || _a === void 0 ? void 0 : _a.resolution) !== null && _b !== void 0 ? _b : 1;
                        resolution = (typeof resolutionPref === 'number') ? [resolutionPref] : resolutionPref;
                        if (!((_c = options.texturePreference) === null || _c === void 0 ? void 0 : _c.format)) { return [3 /*break*/, 9]; }
                        formatPref = (_d = options.texturePreference) === null || _d === void 0 ? void 0 : _d.format;
                        formats = (typeof formatPref === 'string') ? [formatPref] : formatPref;
                        _i = 0, _e = this._detections;
                        _h.label = 4;
                    case 4:
                        if (!(_i < _e.length)) { return [3 /*break*/, 8]; }
                        detection = _e[_i];
                        return [4 /*yield*/, detection.test()];
                    case 5:
                        if (!!(_h.sent())) { return [3 /*break*/, 7]; }
                        return [4 /*yield*/, detection.remove(formats)];
                    case 6:
                        formats = _h.sent();
                        _h.label = 7;
                    case 7:
                        _i++;
                        return [3 /*break*/, 4];
                    case 8: return [3 /*break*/, 14];
                    case 9:
                        formats = ['png', 'jpg', 'jpeg'];
                        _f = 0, _g = this._detections;
                        _h.label = 10;
                    case 10:
                        if (!(_f < _g.length)) { return [3 /*break*/, 14]; }
                        detection = _g[_f];
                        return [4 /*yield*/, detection.test()];
                    case 11:
                        if (!_h.sent()) { return [3 /*break*/, 13]; }
                        return [4 /*yield*/, detection.add(formats)];
                    case 12:
                        formats = _h.sent();
                        _h.label = 13;
                    case 13:
                        _f++;
                        return [3 /*break*/, 10];
                    case 14:
                        this.resolver.prefer({
                            params: {
                                format: formats,
                                resolution: resolution,
                            },
                        });
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Allows you to specify how to resolve any assets load requests.
     * There are a few ways to add things here as shown below:
     * @example
     * // simple
     * PIXI.Assets.add('bunnyBooBoo', 'bunny.png');
     * const bunny = await PIXI.Assets.load('bunnyBooBoo');
     *
     * // multiple keys:
     * PIXI.Assets.add(['burger', 'chicken'], 'bunny.png');
     *
     * const bunny = await PIXI.Assets.load('burger');
     * const bunny2 = await PIXI.Assets.load('chicken');
     *
     * // passing options to to the object
     * PIXI.Assets.add(
     *     'bunnyBooBooSmooth',
     *     'bunny{png,webp}',
     *     {scaleMode:SCALE_MODES.NEAREST} // base texture options
     * );
     *
     * // multiple assets,
     *
     * // the following all do the same thing:
     *
     * PIXI.Assets.add('bunnyBooBoo', 'bunny{png,webp}');
     *
     * PIXI.Assets.add('bunnyBooBoo', [
     * 'bunny.png',
     * 'bunny.webp'
     * ]);
     *
     * PIXI.Assets.add('bunnyBooBoo', [
     *    {
     *       format:'png',
     *       src:'bunny.png',
     *    },
     *    {
     *       format:'webp',
     *       src:'bunny.webp',
     *    }
     * ]);
     *
     * const bunny = await PIXI.Assets.load('bunnyBooBoo'); // will try to load WebP if available
     * @param keysIn - the key or keys that you will reference when loading this asset
     * @param assetsIn - the asset or assets that will be chosen from when loading via the specified key
     * @param data - asset-specific data that will be passed to the loaders
     * - Useful if you want to initiate loaded objects with specific data
     */
    AssetsClass.prototype.add = function (keysIn, assetsIn, data) {
        this.resolver.add(keysIn, assetsIn, data);
    };
    /**
     * Loads your assets! You pass in a key or URL and it will return a promise that
     * resolves to the loaded asset. If multiple assets a requested, it will return a hash of assets.
     *
     * Don't worry about loading things multiple times, behind the scenes assets are only ever loaded
     * once and the same promise reused behind the scenes so you can safely call this function multiple
     * times with the same key and it will always return the same asset.
     * @example
     * // load a URL:
     * const myImageTexture = await PIXI.Assets.load('http://some.url.com/image.png'); // => returns a texture
     *
     * PIXI.Assets.add('thumper', 'bunny.png');
     * PIXI.Assets.add('chicko', 'chicken.png');
     *
     * // load multiple assets:
     * const textures = await PIXI.Assets.load(['thumper', 'chicko']); // => {thumper: Texture, chicko: Texture}
     * @param urls - the urls to load
     * @param onProgress - optional function that is called when progress on asset loading is made.
     * The function is passed a single parameter, `progress`, which represents the percentage
     * (0.0 - 1.0) of the assets loaded.
     * @returns - the assets that were loaded, either a single asset or a hash of assets
     */
    AssetsClass.prototype.load = function (urls, onProgress) {
        return __awaiter(this, void 0, Promise, function () {
            var singleAsset, urlArray, resolveResults, out;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!!this._initialized) { return [3 /*break*/, 2]; }
                        return [4 /*yield*/, this.init()];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2:
                        singleAsset = isSingleItem(urls);
                        urlArray = convertToList(urls)
                            .map(function (url) {
                            if (typeof url !== 'string') {
                                _this.resolver.add(url.src, url);
                                return url.src;
                            }
                            return url;
                        });
                        resolveResults = this.resolver.resolve(urlArray);
                        return [4 /*yield*/, this._mapLoadToResolve(resolveResults, onProgress)];
                    case 3:
                        out = _a.sent();
                        return [2 /*return*/, singleAsset ? out[urlArray[0]] : out];
                }
            });
        });
    };
    /**
     * This adds a bundle of assets in one go so that you can load them as a group.
     * For example you could add a bundle for each screen in you pixi app
     * @example
     *  PIXI.Assets.addBundle('animals', {
     *    bunny: 'bunny.png',
     *    chicken: 'chicken.png',
     *    thumper: 'thumper.png',
     *  });
     *
     * const assets = await PIXI.Assets.loadBundle('animals');
     * @param bundleId - the id of the bundle to add
     * @param assets - a record of the asset or assets that will be chosen from when loading via the specified key
     */
    AssetsClass.prototype.addBundle = function (bundleId, assets) {
        this.resolver.addBundle(bundleId, assets);
    };
    /**
     * Bundles are a way to load multiple assets at once.
     * If a manifest has been provided to the init function then you can load a bundle, or bundles.
     * you can also add bundles via `addBundle`
     * @example
     * // manifest example
     * const manifest = {
     *   bundles:[{
     *      name:'load-screen',
     *      assets:[
     *          {
     *             name: 'background',
     *             srcs: 'sunset.png',
     *          },
     *          {
     *             name: 'bar',
     *             srcs: 'load-bar.{png,webp}',
     *          }
     *      ]
     *   },
     *   {
     *      name:'game-screen',
     *      assets:[
     *          {
     *             name: 'character',
     *             srcs: 'robot.png',
     *          },
     *          {
     *             name: 'enemy',
     *             srcs: 'bad-guy.png',
     *          }
     *      ]
     *   }]
     * }}
     *
     * await Asset.init({
     *  manifest
     * });
     *
     * // load a bundle..
     * loadScreenAssets = await PIXI.Assets.loadBundle('load-screen');
     * // load another..
     * gameScreenAssets = await PIXI.Assets.loadBundle('game-screen');
     * @param bundleIds - the bundle id or ids to load
     * @param onProgress - optional function that is called when progress on asset loading is made.
     * The function is passed a single parameter, `progress`, which represents the percentage (0.0 - 1.0)
     * of the assets loaded.
     * @returns all the bundles assets or a hash of assets for each bundle specified
     */
    AssetsClass.prototype.loadBundle = function (bundleIds, onProgress) {
        return __awaiter(this, void 0, Promise, function () {
            var singleAsset, resolveResults, out, keys, count, total, _onProgress, promises;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!!this._initialized) { return [3 /*break*/, 2]; }
                        return [4 /*yield*/, this.init()];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2:
                        singleAsset = false;
                        if (typeof bundleIds === 'string') {
                            singleAsset = true;
                            bundleIds = [bundleIds];
                        }
                        resolveResults = this.resolver.resolveBundle(bundleIds);
                        out = {};
                        keys = Object.keys(resolveResults);
                        count = 0;
                        total = 0;
                        _onProgress = function () {
                            onProgress === null || onProgress === void 0 ? void 0 : onProgress(++count / total);
                        };
                        promises = keys.map(function (bundleId) {
                            var resolveResult = resolveResults[bundleId];
                            total += Object.keys(resolveResult).length;
                            return _this._mapLoadToResolve(resolveResult, _onProgress)
                                .then(function (resolveResult) {
                                out[bundleId] = resolveResult;
                            });
                        });
                        return [4 /*yield*/, Promise.all(promises)];
                    case 3:
                        _a.sent();
                        return [2 /*return*/, singleAsset ? out[bundleIds[0]] : out];
                }
            });
        });
    };
    /**
     * Initiate a background load of some assets. it will passively begin to load these assets in the background.
     * So when you actually come to loading them you will get a promise that resolves to the loaded assets immediately
     *
     * An example of this might be that you would background load game assets after your inital load.
     * then when you got to actually load your game screen assets when a player goes to the game - the loading
     * would already have stared or may even be complete, saving you having to show an interim load bar.
     * @example
     * PIXI.Assets.backgroundLoad('bunny.png');
     *
     * // later on in your app...
     * await PIXI.Assets.loadBundle('bunny.png'); // will resolve quicker as loading may have completed!
     * @param urls - the url / urls you want to background load
     */
    AssetsClass.prototype.backgroundLoad = function (urls) {
        return __awaiter(this, void 0, Promise, function () {
            var resolveResults;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!!this._initialized) { return [3 /*break*/, 2]; }
                        return [4 /*yield*/, this.init()];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2:
                        if (typeof urls === 'string') {
                            urls = [urls];
                        }
                        resolveResults = this.resolver.resolve(urls);
                        this._backgroundLoader.add(Object.values(resolveResults));
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Initiate a background of a bundle, works exactly like backgroundLoad but for bundles.
     * this can only be used if the loader has been initiated with a manifest
     * @example
     * await PIXI.Assets.init({
     *    manifest: {
     *       bundles: [
     *       {
     *          name:'load-screen',
     *          assets:[...]
     *       }
     *       ...]
     *   }
     * });
     *
     * PIXI.Assets.backgroundLoadBundle('load-screen');
     *
     * // later on in your app...
     * await PIXI.Assets.loadBundle('load-screen'); // will resolve quicker as loading may have completed!
     * @param bundleIds - the bundleId / bundleIds you want to background load
     */
    AssetsClass.prototype.backgroundLoadBundle = function (bundleIds) {
        return __awaiter(this, void 0, Promise, function () {
            var resolveResults;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!!this._initialized) { return [3 /*break*/, 2]; }
                        return [4 /*yield*/, this.init()];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2:
                        if (typeof bundleIds === 'string') {
                            bundleIds = [bundleIds];
                        }
                        resolveResults = this.resolver.resolveBundle(bundleIds);
                        Object.values(resolveResults).forEach(function (resolveResult) {
                            _this._backgroundLoader.add(Object.values(resolveResult));
                        });
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Only intended for development purposes.
     * This will wipe the resolver and caches.
     * You will need to reinitialize the Asset
     */
    AssetsClass.prototype.reset = function () {
        this.resolver.reset();
        this.loader.reset();
        this.cache.reset();
        this._initialized = false;
    };
    /**
     * Instantly gets an asset already loaded from the cache. If the asset has not yet been loaded,
     * it will return undefined. So it's on you! When in doubt just use `PIXI.Assets.load` instead.
     * (remember, the loader will never load things more than once!)
     * @param keys - The key or keys for the assets that you want to access
     * @returns - The assets or hash of assets requested
     */
    AssetsClass.prototype.get = function (keys) {
        if (typeof keys === 'string') {
            return Cache.get(keys);
        }
        var assets = {};
        for (var i = 0; i < keys.length; i++) {
            assets[i] = Cache.get(keys[i]);
        }
        return assets;
    };
    /**
     * helper function to map resolved assets back to loaded assets
     * @param resolveResults - the resolve results from the resolver
     * @param onProgress - the progress callback
     */
    AssetsClass.prototype._mapLoadToResolve = function (resolveResults, onProgress) {
        return __awaiter(this, void 0, Promise, function () {
            var resolveArray, resolveKeys, loadedAssets, out;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        resolveArray = Object.values(resolveResults);
                        resolveKeys = Object.keys(resolveResults);
                        // pause background loader...
                        this._backgroundLoader.active = false;
                        return [4 /*yield*/, this.loader.load(resolveArray, onProgress)];
                    case 1:
                        loadedAssets = _a.sent();
                        // resume background loader...
                        this._backgroundLoader.active = true;
                        out = {};
                        resolveArray.forEach(function (resolveResult, i) {
                            var asset = loadedAssets[resolveResult.src];
                            var keys = [resolveResult.src];
                            if (resolveResult.alias) {
                                keys.push.apply(keys, resolveResult.alias);
                            }
                            out[resolveKeys[i]] = asset;
                            Cache.set(keys, asset);
                        });
                        return [2 /*return*/, out];
                }
            });
        });
    };
    /**
     * Unload an asset or assets. As the Assets class is responsible for creating the assets via the `load` function
     * this will make sure to destroy any assets and release them from memory.
     * Once unloaded, you will need to load the asset again.
     *
     * Use this to help manage assets if you find that you have a large app and you want to free up memory.
     *
     * * it's up to you as the developer to make sure that textures are not actively being used when you unload them,
     * Pixi won't break but you will end up with missing assets. Not a good look for the user!
     * @example
     * // load a URL:
     * const myImageTexture = await PIXI.Assets.load('http://some.url.com/image.png'); // => returns a texture
     *
     * await PIXI.Assets.unload('http://some.url.com/image.png')
     *
     * myImageTexture <-- will now be destroyed.
     *
     * // unload multiple assets:
     * const textures = await PIXI.Assets.unload(['thumper', 'chicko']);
     * @param urls - the urls to unload
     */
    AssetsClass.prototype.unload = function (urls) {
        return __awaiter(this, void 0, Promise, function () {
            var urlArray, resolveResults;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!!this._initialized) { return [3 /*break*/, 2]; }
                        return [4 /*yield*/, this.init()];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2:
                        urlArray = convertToList(urls)
                            .map(function (url) {
                            return ((typeof url !== 'string') ? url.src : url);
                        });
                        resolveResults = this.resolver.resolve(urlArray);
                        return [4 /*yield*/, this._unloadFromResolved(resolveResults)];
                    case 3:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Bundles are a way to manage multiple assets at once.
     * this will unload all files in a bundle.
     *
     * once a bundle has been unloaded, you need to load it again to have access to the assets.
     * @example
     * PIXI.Assets.addBundle({
     *   'thumper': 'http://some.url.com/thumper.png',
     * })
     *
     * const assets = await PIXI.Assets.loadBundle('thumper');
     *
     * // now to unload..
     *
     * await await PIXI.Assets.unloadBundle('thumper');
     *
     * // all assets in the assets object will now have been destroyed and purged from the cache
     * @param bundleIds - the bundle id or ids to unload
     */
    AssetsClass.prototype.unloadBundle = function (bundleIds) {
        return __awaiter(this, void 0, Promise, function () {
            var resolveResults, promises;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!!this._initialized) { return [3 /*break*/, 2]; }
                        return [4 /*yield*/, this.init()];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2:
                        bundleIds = convertToList(bundleIds);
                        resolveResults = this.resolver.resolveBundle(bundleIds);
                        promises = Object.keys(resolveResults).map(function (bundleId) {
                            return _this._unloadFromResolved(resolveResults[bundleId]);
                        });
                        return [4 /*yield*/, Promise.all(promises)];
                    case 3:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    AssetsClass.prototype._unloadFromResolved = function (resolveResult) {
        return __awaiter(this, void 0, void 0, function () {
            var resolveArray;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        resolveArray = Object.values(resolveResult);
                        resolveArray.forEach(function (resolveResult) {
                            Cache.remove(resolveResult.src);
                        });
                        return [4 /*yield*/, this.loader.unload(resolveArray)];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    Object.defineProperty(AssetsClass.prototype, "detections", {
        /** All the detection parsers currently added to the Assets class. */
        get: function () {
            return this._detections;
        },
        enumerable: false,
        configurable: true
    });
    return AssetsClass;
}());
var Assets = new AssetsClass();
// Handle registration of extensions
extensions$1
    .handleByList(ExtensionType.LoadParser, Assets.loader.parsers)
    .handleByList(ExtensionType.ResolveParser, Assets.resolver.parsers)
    .handleByList(ExtensionType.CacheParser, Assets.cache.parsers)
    .handleByList(ExtensionType.DetectionParser, Assets.detections);
extensions$1.add(loadTextures, loadSVG, loadTxt, loadJson, loadSpritesheet, loadBitmapFont, loadWebFont, 
// cache extensions
cacheSpritesheet, cacheTextureArray, 
// resolve extensions
resolveTextureUrl, resolveSpriteSheetUrl, 
// detection extensions
detectWebp, detectAvif);

export { Assets, AssetsClass, Cache, addFormats, cacheSpritesheet, cacheTextureArray, convertToList, createStringVariations, detectAvif, detectBasis, detectCompressedTextures, detectWebp, getFontFamilyName, isSingleItem, loadBasis, loadBitmapFont, loadDDS, loadImageBitmap, loadJson, loadKTX, loadSVG, loadSpritesheet, loadTextures, loadTxt, loadWebFont, removeFormats, resolveCompressedTextureUrl, resolveSpriteSheetUrl, resolveTextureUrl };
//# sourceMappingURL=assets.mjs.map
